<template>
  <div ref="container" class="">
    <div ref="group" class="flex flex-row" :class="[!widthFull && 'w-fit', widthFull && 'w-full']">
      <template v-for="(buttonConfig, index) in regularButtons" :key="index">
        <BaseTextButton
          v-if="buttonConfig.type == 'text'"
          v-bind="buttonConfig"
          @click="buttonConfig.callback"
          >{{ buttonConfig.label }}</BaseTextButton
        >
        <BaseIconButton
          v-if="buttonConfig.type == 'icon'"
          v-tooltip="buttonConfig.label"
          v-bind="buttonConfig"
          @click="buttonConfig.callback"
        />
      </template>
      <BaseMenu
        v-if="menuButtons.length > 0"
        :items="
          menuButtons.map((buttonConfig, index) => ({
            id: index.toString(),
            title: buttonConfig.label,
            leadingIcon: buttonConfig.icon,
            callback: buttonConfig.callback,
            destructive: buttonConfig.destructive,
          }))
        "
        v-slot="{ open }"
      >
        <BaseIconButton
          :size="size"
          icon="more_vert"
          :color="menuButtonColor"
          width="narrow"
          :class="marginClassForMoreButton"
          :force-pressed="open"
        />
      </BaseMenu>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'

  import { useElementBounding } from '@vueuse/core'
  import type { FlexibelButtonConfig, FlexibelButtonProps } from './button-props'
  import BaseTextButton from './text-button/BaseTextButton.vue'
  import BaseIconButton from './icon-button/BaseIconButton.vue'
  import BaseMenu from '../menu/BaseMenu.vue'
  import type { ButtonSize } from './button-size'
  import type { ButtonColor } from './button-color'
  interface Props {
    size?: ButtonSize
    menuButtonColor?: Exclude<ButtonColor, 'text' | 'elevated'> | 'standard'
    buttons: FlexibelButtonConfig[]
    widthFull?: boolean
  }

  const {
    buttons: configuredButtons,
    widthFull,
    size = 'S',
    menuButtonColor = 'tonal',
  } = defineProps<Props>()

  const actualConfiguredButtons = computed(() =>
    configuredButtons?.filter(
      (config) => config.condition == undefined || config.condition == true,
    ),
  )

  const calculatedConfiguredButtons = computed(() => {
    return [...computedConfigurationMap.value.values()]
  })

  const computedConfigurationMap = ref(new Map<string, FlexibelButtonProps>())

  const container = useTemplateRef<HTMLDivElement>('container')
  const group = useTemplateRef<HTMLDivElement>('group')

  const regularButtons = computed(() => {
    return calculatedConfiguredButtons.value.filter((config) => config.type != 'menu')
  })

  const menuButtons = computed(() => {
    return calculatedConfiguredButtons.value.filter((config) => config.type == 'menu')
  })

  const isInitialized = ref(false)

  const initOrders = () => {
    const prios: number[] = []
    if (actualConfiguredButtons.value) {
      for (const button of actualConfiguredButtons.value) {
        for (const config of button.flexibelConfigurations) {
          if (config.order) {
            prios.push(config.order)
          }
        }
      }
    }

    orders.value = prios.sort()
  }
  const orders = ref<number[]>([])

  onMounted(async () => {
    await initButtons()

    await loopCalculation()

    isInitialized.value = true
  })

  const loopCalculation = async () => {
    if (group.value && container.value) {
      await nextTick()
      let isOverflowing =
        group.value.getBoundingClientRect().width > container.value.getBoundingClientRect().width
      while (isOverflowing && orders.value.length > 0) {
        await calculateConfiguredButtons()
        // await new Promise((r) => setTimeout(r, 200))
        isOverflowing =
          group.value.getBoundingClientRect().width > container.value.getBoundingClientRect().width
      }
    }
  }

  const initButtons = async () => {
    if (actualConfiguredButtons.value) {
      initOrders()
      computedConfigurationMap.value.clear()
      for (const buttonConfig of actualConfiguredButtons.value) {
        const configWithoutOrder = buttonConfig.flexibelConfigurations.find(
          (config) => config.order == undefined,
        )

        if (configWithoutOrder) {
          computedConfigurationMap.value.set(
            buttonConfig.id,
            mapConfig(buttonConfig, configWithoutOrder),
          )
          configWithoutOrder.isUsed = true
        } else {
          for (const order of orders.value) {
            const matchingConfig = buttonConfig.flexibelConfigurations.find(
              (config) => config.order == order,
            )
            if (matchingConfig) {
              computedConfigurationMap.value.set(
                buttonConfig.id,
                mapConfig(buttonConfig, matchingConfig),
              )
              removeOrderEntry(order)
              matchingConfig.isUsed = true
              break
            }
          }
        }
      }
    }
    await nextTick()
  }

  const removeOrderEntry = (order: number) => {
    const index = orders.value.indexOf(order)
    orders.value = [...orders.value.slice(0, index), ...orders.value.slice(index + 1)]
  }

  const mapConfig = (
    buttonConfig: Omit<FlexibelButtonConfig, 'flexibelConfigurations'>,
    flexibelConfig: FlexibelButtonConfig['flexibelConfigurations'][0],
  ): FlexibelButtonProps => {
    const config = {
      label: buttonConfig.label,
      icon: buttonConfig.icon,
      color: buttonConfig.color,
      size: size,
      shape: buttonConfig.shape,
      disabled: buttonConfig.disabled,
      toggle: buttonConfig.toggle,
      selected: buttonConfig.selected,
      trailingIcon: buttonConfig.trailingIcon,
      callback: buttonConfig.callback,
      order: flexibelConfig.order,
      width: flexibelConfig.width,
      destructive: buttonConfig.destructive,
    }
    if (flexibelConfig.type === 'text') {
      return {
        type: 'text',
        ...config,
      }
    }
    if (flexibelConfig.type === 'icon') {
      return {
        type: 'icon',
        ...config,
      }
    }
    return {
      type: 'menu',
      callback: buttonConfig.callback,
      icon: buttonConfig.icon,
      label: buttonConfig.label,
      destructive: buttonConfig.destructive,
    }
  }

  const calculateConfiguredButtons = async () => {
    await document.fonts.ready
    await nextTick()
    if (orders.value.length > 0) {
      const nextOrder = orders.value[0]
      for (const buttonConfig of actualConfiguredButtons.value) {
        const matchingConfig = buttonConfig.flexibelConfigurations.find(
          (config) => config.order == nextOrder && !config.isUsed,
        )
        if (matchingConfig) {
          computedConfigurationMap.value.set(
            buttonConfig.id,
            mapConfig(buttonConfig, matchingConfig),
          )
          removeOrderEntry(nextOrder)
          matchingConfig.isUsed = true
          break
        }
      }
      removeOrderEntry(nextOrder)
    }
    await nextTick()
  }

  const { width: containerWidth } = useElementBounding(container)

  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const debouncedWatcher = (newContainerWidth: number) => {
    if (newContainerWidth && isInitialized.value) {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      timeoutId = setTimeout(async () => {
        initOrders()
        for (const buttonConfig of actualConfiguredButtons.value) {
          for (const config of buttonConfig.flexibelConfigurations) {
            config.isUsed = false
          }
        }
        await initButtons()
        await loopCalculation()
      }, 10)
    }
  }

  watch(containerWidth, (newContainerWidth) => debouncedWatcher(newContainerWidth))
  watch(
    () => configuredButtons,
    async () => {
      isInitialized.value = false
      await initButtons()
      await loopCalculation()
      isInitialized.value = true
    },
  )

  const marginClassForMoreButton = computed(() => {
    if (size == 'XS') {
      return 'ml-[9px]!'
    }

    return 'ml-[4px]!'
  })
</script>
